import { Microphone, Paperclip, PencilSimple } from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import { Tag } from "common/components/ui/Tag";
import useInboxDraftStore from "common/hooks/useInboxDraftStore";

import type { Conversation } from "../../types";

interface MessageDetailsProps {
  conversation: Conversation;
  isMine: boolean;
}

export default function MessageDetails({
  conversation,
  isMine,
}: MessageDetailsProps) {
  const {
    last_message: lastMessage,
    unread_count: unreadCount,
    participants,
    conversation_urn: conversationUrn,
    owner: accountId,
  } = conversation;

  const {
    deleted: isDeleted,
    inmail: isInmail,
    sender,
    body,
    audio,
  } = lastMessage;

  const draftKey = `${conversationUrn}:${accountId}`;

  const { drafts } = useInboxDraftStore();
  const draftMessage = drafts[draftKey];
  const isGroupConversation = participants.length > 1;

  let message = isMine ? (
    <>
      <span className="text-xs text-black-500">You: </span>
      {body}
    </>
  ) : (
    body
  );

  if (isDeleted) {
    message = "This message has been deleted";
  }

  if (draftMessage) {
    return (
      <div className="flex flex-row items-center gap-1 text-xs text-black-400">
        <PencilSimple />

        <span className="line-clamp-1 break-all text-start">
          Draft: {draftMessage}
        </span>
      </div>
    );
  }

  if (isInmail) {
    return (
      <div className="flex flex-row items-center gap-1">
        <Tag size="sm" variant="secondary">
          InMail
        </Tag>

        <span
          className={clsx([
            "line-clamp-1 text-start text-xs text-black-500",
            unreadCount && "max-w-96",
          ])}
        >
          {message}
        </span>
      </div>
    );
  }

  if (!body) {
    return (
      <div
        className={clsx([
          "flex flex-row items-center gap-0.5",
          unreadCount
            ? "text-xs-bold text-black-950"
            : "text-xs text-black-500",
        ])}
      >
        <RenderIf condition={isMine}>
          <span className="text-xs text-black-500">You:</span>
        </RenderIf>

        {audio ? <Microphone /> : <Paperclip />}

        <span className="line-clamp-1 break-all text-start italic">
          {audio ? "Sent a voice message" : "Sent an attachment"}
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-row justify-between">
      <div className="flex flex-row items-center gap-1 text-start leading-normal">
        {/* Render group name if applicable */}
        <RenderIf condition={isGroupConversation && !isMine}>
          <span className="text-nowrap text-xs text-black-500">
            {sender.full_name}:
          </span>
        </RenderIf>

        {/* Message content */}
        <span
          className={clsx([
            "line-clamp-1 w-full text-xs text-black-500",
            unreadCount
              ? "max-w-[40ch] text-xs-bold text-black-950"
              : "max-w-[45ch]",
            isDeleted && "pr-1 italic",
          ])}
        >
          {message}
        </span>
      </div>
    </div>
  );
}
